/**
 * Anchor scroll guard.
 *
 * Problem: If the URL contains a fragment like `#shop`
 * (often left over after clicking an in-page CTA), subsequent reloads / bfcache restores
 * can auto-scroll the page to that section, which feels like a random jump.
 *
 * Fix:
 * - If we load with `#shop` due to a reload/back-forward restore, clear the hash and
 *   restore scroll to top.
 * - When a user clicks an in-page link to `#shop`, perform a smooth scroll but then
 *   remove the hash from the URL so it won't cause future auto-scroll on refresh.
 */
(function anchorScrollGuard() {
  const GUARDED_HASHES = new Set(['#shop']);

  const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // We used to scope this to mobile only, but the same "refresh jumps down" issue
  // can happen on desktop too if the hash is left in the URL.
  const isMobileViewport = () => window.matchMedia('(max-width: 749px)').matches;

  // iOS detection (covers iPadOS reporting as Mac).
  const isIOS =
    /iP(ad|hone|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  function removeHashFromUrl() {
    const url = new URL(window.location.href);
    url.hash = '';
    history.replaceState(history.state, '', url.toString());
  }

  function getNavType() {
    const navEntry = performance.getEntriesByType?.('navigation')?.[0];
    return navEntry && 'type' in navEntry ? navEntry.type : null;
  }

  /**
   * Desktop/Windows symptom: page "jumps" down on first paint / shortly after pageshow.
   * Common causes: scroll restoration, focus/autofocus, or late layout shifts.
   *
   * We detect an early non-user-initiated scroll away from the top and snap back to top.
   * This is intentionally conservative:
   * - only when there's no (non-guarded) hash deep link
   * - stops immediately on user interaction
   * - runs only for a short window after pageshow
   */
  function preventEarlyAutoScrollToMiddle() {
    // Don’t fight legitimate deep links.
    if (window.location.hash && !GUARDED_HASHES.has(window.location.hash)) return;

    let didUserInteract = false;
    const markInteract = () => {
      didUserInteract = true;
      cleanup();
    };

    const onScroll = () => {
      // If user interacted, never interfere.
      if (didUserInteract) return;

      // If we’re not near top, snap back.
      if (window.scrollY > 10) window.scrollTo({ top: 0, behavior: 'instant' });
    };

    const cleanup = () => {
      window.removeEventListener('pointerdown', markInteract, true);
      window.removeEventListener('wheel', markInteract, true);
      window.removeEventListener('keydown', markInteract, true);
      window.removeEventListener('touchstart', markInteract, true);
      window.removeEventListener('scroll', onScroll, true);
    };

    window.addEventListener('pointerdown', markInteract, true);
    window.addEventListener('wheel', markInteract, true);
    window.addEventListener('keydown', markInteract, true);
    window.addEventListener('touchstart', markInteract, true);
    window.addEventListener('scroll', onScroll, true);

    try {
      // Helps prevent browsers from restoring scroll position unexpectedly.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch {
      // ignore
    }

    // Some browsers apply scroll restoration after the first paint; check a few times.
    requestAnimationFrame(() => onScroll());
    setTimeout(onScroll, 0);
    setTimeout(onScroll, 150);
    setTimeout(onScroll, 350);

    // Stop enforcing quickly to avoid fighting normal behavior.
    setTimeout(cleanup, 900);
  }

  /**
   * Some iOS restores scroll position unexpectedly on pageshow even with no hash.
   * We "lock" scroll to the top briefly until the user interacts.
   */
  function lockScrollToTopBriefly() {
    if (!isIOS) return;
    if (!isMobileViewport()) return;

    // Don’t fight legitimate deep links.
    if (window.location.hash && !GUARDED_HASHES.has(window.location.hash)) return;

    // If this is a true back/forward restore, users often expect scroll restore.
    // But you explicitly don’t want the auto-scroll on iOS, so we only enforce
    // for a brief window (stops once the user interacts).
    let unlocked = false;
    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('touchstart', unlock, true);
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('wheel', unlock, true);
      window.removeEventListener('keydown', unlock, true);
      window.removeEventListener('scroll', onScroll, true);
    };

    const onScroll = () => {
      if (unlocked) return;
      if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: 'instant' });
    };

    window.addEventListener('touchstart', unlock, true);
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('wheel', unlock, true);
    window.addEventListener('keydown', unlock, true);
    window.addEventListener('scroll', onScroll, true);

    try {
      // Helps prevent Safari from restoring scroll position.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch {
      // ignore
    }

    // Force top a couple times (iOS can apply restoration after initial paint).
    window.scrollTo({ top: 0, behavior: 'instant' });
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'instant' }));
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 250);

    // Stop enforcing after a short window even without interaction.
    setTimeout(unlock, 1500);
  }

  // Prevent unexpected auto-scroll on reload / bfcache restore with leftover hash.
  window.addEventListener('pageshow', () => {
    const navType = getNavType();

    // iOS can restore scroll even without a hash; lock briefly.
    lockScrollToTopBriefly();

    // Cross-browser: prevent early unexplained jumps away from the top.
    // (This addresses Windows/desktop reports too.)
    preventEarlyAutoScrollToMiddle();

    // If we have a guarded hash, clear it (prevents future auto-scroll).
    if (GUARDED_HASHES.has(window.location.hash) && (navType === 'reload' || navType === 'back_forward')) {
      removeHashFromUrl();
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  });

  // If something sets location.hash = "#shop", clear it after the scroll.
  window.addEventListener('hashchange', () => {
    if (!GUARDED_HASHES.has(window.location.hash)) return;
    // Let browser perform anchor scroll, then remove the hash.
    setTimeout(removeHashFromUrl, 0);
  });

  // Intercept clicks to guarded hashes, scroll, then remove hash from URL.
  document.addEventListener(
    'click',
    (event) => {
      const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
      const link = target?.closest?.('a[href^="#"]');
      if (!(link instanceof HTMLAnchorElement)) return;

      const href = link.getAttribute('href') || '';
      if (!GUARDED_HASHES.has(href)) return;

      const anchorTarget = document.querySelector(href);
      if (!(anchorTarget instanceof HTMLElement)) return;

      event.preventDefault();
      anchorTarget.scrollIntoView({
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'start',
      });

      // Remove the hash so refresh/back doesn't auto-scroll.
      removeHashFromUrl();
    },
    { capture: true }
  );
})();

